import { format, parseISO } from 'date-fns';
import { getExpenseCategory, getCategoryStyle, formatCurrency } from '../../utils/categories';
import { getCategoryIcon } from '../UI/Icons';

/**
 * ExpenseMobileCard - Mobile card view for a single expense
 */
export default function ExpenseMobileCard({
  expense,
  isEditing,
  editAmount,
  onEditAmountChange,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onTogglePlanned,
  onDelete,
}) {
  const category = getExpenseCategory(expense.category);
  const categoryStyle = getCategoryStyle(category.colorVar);

  return (
    <div 
      className="p-4 transition-colors"
      style={{ borderBottom: '1px solid var(--color-border-subtle)' }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          {/* Category Icon */}
          <div 
            className="flex-shrink-0 w-10 h-10 rounded-md flex items-center justify-center"
            style={categoryStyle}
          >
            {getCategoryIcon(expense.category, { size: 20 })}
          </div>
          
          {/* Details */}
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate" style={{ color: 'var(--color-text-primary)' }}>
              {expense.description}
            </div>
            <div className="text-xs mt-0.5" style={{ color: 'var(--color-text-muted)' }}>
              {format(parseISO(expense.expense_date), 'dd MMM yyyy')}
            </div>
            <div className="flex items-center gap-2 mt-1">
              <button
                onClick={() => onTogglePlanned(expense)}
                className="badge text-xs cursor-pointer"
                style={expense.is_planned ? {
                  backgroundColor: 'rgba(189, 166, 110, 0.15)',
                  color: 'var(--color-category-6)',
                } : {
                  backgroundColor: 'rgba(78, 143, 106, 0.15)',
                  color: 'var(--color-status-success)',
                }}
              >
                {expense.is_planned ? 'Planned' : 'Actual'}
              </button>
            </div>
          </div>
        </div>

        {/* Amount & Actions */}
        <div className="flex flex-col items-end gap-2">
          {isEditing ? (
            <input
              type="number"
              value={editAmount}
              onChange={(e) => onEditAmountChange(e.target.value)}
              className="input w-24 text-right text-sm py-1"
              min="0"
              step="0.01"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') onSaveEdit(expense);
                if (e.key === 'Escape') onCancelEdit();
              }}
            />
          ) : (
            <div 
              className="text-sm font-semibold"
              style={{ color: 'var(--color-text-primary)' }}
              onClick={() => onStartEdit(expense)}
            >
              {formatCurrency(expense.amount, expense.currency)}
            </div>
          )}
          
          <div className="flex items-center gap-1">
            {isEditing ? (
              <>
                <button
                  onClick={() => onSaveEdit(expense)}
                  className="p-1.5 rounded"
                  style={{ color: 'var(--color-status-success)' }}
                >
                  <CheckIcon className="w-4 h-4" />
                </button>
                <button
                  onClick={onCancelEdit}
                  className="p-1.5 rounded"
                  style={{ color: 'var(--color-text-muted)' }}
                >
                  <XIcon className="w-4 h-4" />
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => onStartEdit(expense)}
                  className="p-1.5 rounded"
                  style={{ color: 'var(--color-text-muted)' }}
                >
                  <EditIcon className="w-4 h-4" />
                </button>
                <button
                  onClick={() => onDelete(expense)}
                  className="p-1.5 rounded"
                  style={{ color: 'var(--color-text-muted)' }}
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function TrashIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
  );
}

function EditIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
    </svg>
  );
}

function CheckIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  );
}

function XIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}
